import { useCallback, useRef, useState } from 'react';

/**
 * Server-authoritative Harral Mind Leech (378) unit steal fly animations.
 * Hides the stolen unit on the destination slot until the fly completes.
 * Clears source-slot death tracking so the empty source slot is not treated as a kill.
 */
export const useHarralMindLeechUnitStealFlyAnimations = (
  suppressSourceSlotKeysRef,
  hiddenStolenInstanceIdsRef,
  clearSlotPreviousStateRef = null
) => {
  const [activeFlyGrants, setActiveFlyGrants] = useState([]);
  const consumedFlyAnimationKeysRef = useRef(new Set());

  const queueGrants = useCallback((grants) => {
    if (!grants?.length) {
      return;
    }
    if (!suppressSourceSlotKeysRef?.current || !hiddenStolenInstanceIdsRef?.current) {
      throw new Error('useHarralMindLeechUnitStealFlyAnimations: suppress and hidden refs are required');
    }

    const grantsToQueue = [];
    grants.forEach((grant) => {
      if (!grant?.key || !grant?.stolenUnitInstanceId) {
        throw new Error('useHarralMindLeechUnitStealFlyAnimations.queueGrants: key and stolenUnitInstanceId are required');
      }
      if (consumedFlyAnimationKeysRef.current.has(grant.key)) {
        return;
      }
      consumedFlyAnimationKeysRef.current.add(grant.key);
      suppressSourceSlotKeysRef.current.add(`${grant.fromZone}_${grant.fromIndex}`);
      hiddenStolenInstanceIdsRef.current.add(String(grant.stolenUnitInstanceId));
      if (typeof clearSlotPreviousStateRef?.current === 'function') {
        clearSlotPreviousStateRef.current(grant.fromZone, grant.fromIndex);
      }
      grantsToQueue.push(grant);
    });

    if (grantsToQueue.length === 0) {
      return;
    }

    setActiveFlyGrants((prev) => {
      const existingKeys = new Set(prev.map((g) => g.key));
      const merged = [...prev];
      grantsToQueue.forEach((grant) => {
        if (!existingKeys.has(grant.key)) {
          merged.push(grant);
        }
      });
      return merged;
    });
  }, [suppressSourceSlotKeysRef, hiddenStolenInstanceIdsRef, clearSlotPreviousStateRef]);

  const dismissFlyGrant = useCallback((key, grant) => {
    setActiveFlyGrants((prev) => prev.filter((g) => g.key !== key));
    // Reveal the real unit as soon as the fly lands.
    if (grant?.stolenUnitInstanceId != null) {
      hiddenStolenInstanceIdsRef.current.delete(String(grant.stolenUnitInstanceId));
    }
    // Keep source-slot death/damage suppress briefly so late FX from the same turn-played cannot land.
    if (grant?.fromZone != null && grant?.fromIndex != null) {
      const sourceSlotKey = `${grant.fromZone}_${grant.fromIndex}`;
      window.setTimeout(() => {
        suppressSourceSlotKeysRef.current.delete(sourceSlotKey);
      }, 1500);
    }
  }, [suppressSourceSlotKeysRef, hiddenStolenInstanceIdsRef]);

  return {
    activeFlyGrants,
    dismissFlyGrant,
    queueGrants,
  };
};

export default useHarralMindLeechUnitStealFlyAnimations;
